import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ChevronRight, TrendingUp, DollarSign, Briefcase, Rocket, Target, Star, BarChart3, Zap } from "lucide-react";
import FloatingSocials from "./FloatingSocials";
import { specMove } from "./glass";

const LINES = ["Everything you need to grow", "your social media in one place"];

const BURST_ICONS = [TrendingUp, DollarSign, Briefcase, Rocket, Target, Star, BarChart3, Zap];

const cardBase =
  "glass glass-sheen glass-spec pointer-events-auto cursor-default rounded-[22px]";

const ThumbnailCard = () => (
  <div onMouseMove={specMove} className={`${cardBase} w-40 p-2.5`}>
    <div className="relative flex aspect-[4/3] items-center justify-center rounded-xl bg-gradient-to-br from-[#ececf0] to-[#cfcfd6]">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/95 shadow-sm">
        <div className="ml-0.5 h-0 w-0 border-y-[5px] border-l-[8px] border-y-transparent border-l-[#1d1d1f]" />
      </div>
      <span className="absolute bottom-1.5 right-1.5 h-1.5 w-8 rounded-full bg-black/25" />
    </div>
    <div className="mt-2.5 space-y-1.5 px-0.5 pb-0.5">
      <div className="h-1.5 w-4/5 rounded-full bg-[#1d1d1f]/80" />
      <div className="h-1.5 w-3/5 rounded-full bg-black/15" />
    </div>
  </div>
);

const AnalyticsCard = () => (
  <div onMouseMove={specMove} className={`${cardBase} w-44 p-4`}>
    <div className="flex items-center justify-between">
      <span className="eyebrow text-[#86868b]">Views</span>
      <span className="h-1.5 w-1.5 rounded-full bg-[#16a34a]" />
    </div>
    <svg viewBox="0 0 120 44" className="mt-3 w-full" fill="none">
      <path
        d="M2 38 C 20 37, 30 33, 46 30 S 74 20, 88 15 S 112 5, 118 3 L118 44 L2 44 Z"
        fill="rgba(22,163,74,0.09)"
        stroke="none"
      />
      <path
        d="M2 38 C 20 37, 30 33, 46 30 S 74 20, 88 15 S 112 5, 118 3"
        stroke="#16a34a"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
    <div className="mt-3 flex gap-1.5">
      {[0.9, 0.55, 0.7, 0.35].map((o, i) => (
        <span key={i} className="h-1 flex-1 rounded-full bg-black" style={{ opacity: 0.08 + o * 0.1 }} />
      ))}
    </div>
  </div>
);

const TitleCard = () => (
  <div onMouseMove={specMove} className={`${cardBase} w-44 p-4`}>
    <span className="eyebrow text-[#86868b]">Title</span>
    <div className="mt-3 space-y-2">
      <div className="h-2 w-full rounded-full bg-[#1d1d1f]" />
      <div className="h-2 w-2/3 rounded-full bg-[#1d1d1f]/70" />
      <div className="h-1.5 w-1/3 rounded-full bg-black/15" />
    </div>
    <div className="mt-3 flex gap-1.5">
      <span className="h-3.5 w-10 rounded-full border border-black/15" />
      <span className="h-3.5 w-7 rounded-full border border-black/15" />
    </div>
  </div>
);

const TimelineCard = () => (
  <div onMouseMove={specMove} className={`${cardBase} w-48 p-4`}>
    <span className="eyebrow text-[#86868b]">Timeline</span>
    <div className="mt-3 flex h-7 items-stretch gap-1 rounded-lg bg-black/[0.05] p-1">
      <div className="w-1/3 rounded-md bg-[#1d1d1f]" />
      <div className="w-1/4 rounded-md bg-[#86868b]" />
      <div className="flex-1 rounded-md bg-[#d2d2d7]" />
    </div>
    <div className="relative mt-2.5 h-1 rounded-full bg-black/10">
      <div className="absolute left-2/3 -top-[3px] h-2.5 w-px bg-[#1d1d1f]" />
    </div>
  </div>
);

const FloatLayer = ({ mx, my, depth, className, delay = 0, children }) => {
  const x = useSpring(useTransform(mx, (v) => v * depth), { stiffness: 40, damping: 18 });
  const y = useSpring(useTransform(my, (v) => v * depth * 0.7), { stiffness: 40, damping: 18 });
  return (
    <motion.div style={{ x, y }} className={`absolute ${className}`}>
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.92 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay, duration: 1, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 7 + depth * 0.05, repeat: Infinity, ease: "easeInOut", delay }}
        >
          {children}
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

function HeroVisuals({ mx, my }) {
  return (
    <div className="absolute inset-0 hidden lg:block pointer-events-none opacity-70" aria-hidden="true">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.035) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 42%, black, transparent)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 42%, black, transparent)",
        }}
      />
      <svg
        className="absolute left-1/2 top-[30%] w-[900px] -translate-x-1/2"
        viewBox="0 0 860 210"
        fill="none"
      >
        <defs>
          <linearGradient id="heroGrowthFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#16a34a" stopOpacity="0.13" />
            <stop offset="100%" stopColor="#16a34a" stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.path
          d="M10 178 C 160 172, 260 152, 380 140 S 560 98, 670 68 S 800 24, 836 14 L836 210 L10 210 Z"
          fill="url(#heroGrowthFill)"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.9, duration: 1.4 }}
        />
        <motion.path
          d="M10 178 C 160 172, 260 152, 380 140 S 560 98, 670 68 S 800 24, 836 14"
          stroke="#16a34a"
          strokeWidth="2"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2.8, delay: 0.7, ease: "easeInOut" }}
        />
        <motion.polygon
          points="830,6 846,14 830,22"
          fill="#16a34a"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 3.3, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        />
      </svg>
      <FloatLayer mx={mx} my={my} depth={34} className="left-[6%] top-[26%] -rotate-6" delay={1.1}>
        <ThumbnailCard />
      </FloatLayer>
      <FloatLayer mx={mx} my={my} depth={-42} className="right-[6%] top-[22%] rotate-[5deg]" delay={1.25}>
        <AnalyticsCard />
      </FloatLayer>
      <FloatLayer mx={mx} my={my} depth={-26} className="left-[11%] bottom-[10%] rotate-[3deg]" delay={1.4}>
        <TitleCard />
      </FloatLayer>
      <FloatLayer mx={mx} my={my} depth={28} className="right-[10%] bottom-[8%] -rotate-[4deg]" delay={1.55}>
        <TimelineCard />
      </FloatLayer>
    </div>
  );
}

const TalkButton = ({ onContact }) => {
  const [burst, setBurst] = useState(0);
  return (
    <span className="relative inline-flex" onMouseEnter={() => setBurst((b) => b + 1)}>
      {burst > 0 && (
        <span key={burst} className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
          {BURST_ICONS.map((Icon, p) => {
            const angle = (p / BURST_ICONS.length) * Math.PI * 2 + burst * 0.7;
            const dist = 74 + ((p * 41) % 56);
            return (
              <motion.span
                key={`${burst}-${p}`}
                initial={{ x: 0, y: 0, opacity: 1, scale: 0.3, rotate: 0 }}
                animate={{
                  x: Math.cos(angle) * dist,
                  y: Math.sin(angle) * dist,
                  opacity: 0,
                  scale: 1,
                  rotate: (p % 2 ? 1 : -1) * 120,
                }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="absolute left-1/2 top-1/2 -ml-2 -mt-2 text-[#1d1d1f]"
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
              </motion.span>
            );
          })}
        </span>
      )}
      <button
        onClick={onContact}
        onMouseMove={specMove}
        data-testid="hero-cta-talk"
        className="glass-strong glass-sheen glass-spec relative z-10 rounded-full px-8 py-3.5 text-[15px] font-medium text-white transition-transform duration-300 hover:scale-[1.05] active:scale-[0.95]"
      >
        Talk to us
      </button>
    </span>
  );
};

export default function Hero({ onContact }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative overflow-hidden pt-40 pb-32 sm:pt-48"
      data-testid="hero-section"
    >
      <HeroVisuals mx={mx} my={my} />
      <FloatingSocials />
      <div
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background:
            "radial-gradient(ellipse 90% 75% at 50% 45%, transparent 55%, rgba(0,0,0,0.09))",
        }}
        aria-hidden="true"
      />
      <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 text-center">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.7 }}
          className="eyebrow text-[#86868b]"
        >
          A content growth partner for creators, founders, and businesses
        </motion.p>

        <h1
          className="mt-5 font-display font-semibold tracking-[-0.03em] leading-[1.05] text-5xl sm:text-6xl lg:text-7xl text-[#1d1d1f]"
          data-testid="hero-headline"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.25 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-2xl mx-auto text-[#6e6e73] text-lg sm:text-xl leading-relaxed"
        >
          <p className="text-[#1d1d1f] font-medium">
            Stop wasting time and money on content that doesn't perform.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex items-center justify-center gap-5"
        >
          <TalkButton onContact={onContact} />
          <a
            href="#services"
            onMouseMove={specMove}
            data-testid="hero-cta-explore"
            className="glass-soft glass-spec group flex items-center gap-1.5 rounded-full px-6 py-3.5 text-[15px] font-medium text-[#1d1d1f] transition-transform duration-300 hover:scale-[1.04] active:scale-[0.96]"
          >
            See what we do
            <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
